
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { formRules } from "@/utils/formRules";
import { MylobyPlace } from "@/interfaces/map";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiCursorDefaultClickOutline, mdiMapMarker } from "@mdi/js";
import { stringComparaison, distanceGreaterThan, getMapLink } from "@/utils/misc";
import SuggestionLink from "./buildings/suggestion-link.vue";
import { Getter } from "vuex-class";


@Component({
  components: {
    SvgIcon,
    SuggestionLink,
  },
})
export default class AddressGeo extends Vue {

  @Prop({ default: false })
  public readonly: boolean;

  @Prop({ default: null })
  public address: string;

  @Prop({ default: null })
  public zipcode: string;

  @Prop({ default: null })
  public city: string;

  @Prop({ default: null })
  public country: string;

  @Prop({ default: null })
  public latitude: string;

  @Prop({ default: null })
  public longitude: string;

  @Prop({ default: null })
  public registrationNumber: string;

  @Prop({ default: "GOOGLE" })
  public provider: string;

  public registrationSuggestions: {
    address: string[];
    zipcode: string[];
    city: string[];
    latitude: string[];
    longitude: string[];
    country: string[];
    registrationNumber: string[];
  } = {
    address: [],
    zipcode: [],
    city: [],
    latitude: [],
    longitude: [],
    country: [],
    registrationNumber: [],
  };

  @Getter private isMobile: boolean;

  private mdiMapMarker = mdiMapMarker;

  private loading: boolean = false;
  private items: MylobyPlace[] = [];
  private search: string = null;
  private selected: MylobyPlace | null = null;
  private rules: object = formRules;
  private registrationMissing: boolean = false;
  private searchRegistrationNumber: string = "";

  private mapLink = getMapLink;
  private mdiCursorDefaultClickOutline: any = mdiCursorDefaultClickOutline;

  public async onCompareRegistration() {

    const dataGouvBuilding = await this.$store.dispatch("buildings/getAddressesRegistrationNumber", {
      registrationNumber: this.searchRegistrationNumber,
    });

    if (dataGouvBuilding.status === "error") {
        this.registrationMissing = true;
        this.registrationSuggestions = {
        address: [],
        zipcode: [],
        city: [],
        latitude: [],
        longitude: [],
        country: [],
        registrationNumber: [],
      };
        return;
    }

    const d = dataGouvBuilding.data;
    const overwrittenFields: string[] = [];
    const fieldsMap = {
    registrationNumber: "registrationNumber",
    address: "address",
    zipcode: "zipcode",
    city: "city",
    latitude: "lat",
    longitude: "long",
    country: "country",
    };

    // Overwrite si champ nul
    Object.entries(fieldsMap).forEach(([targetKey, sourceKey]) => {
      if (!this[targetKey]) {
        this.overwriteAddress(d[sourceKey], targetKey);
        overwrittenFields.push(targetKey);
      }
    });

    // Suggestions uniquement si le champ n'a PAS été overwrite
    this.registrationSuggestions = {
      address: overwrittenFields.includes("address")
      ? null
      : (!stringComparaison(d.address, this.address) ? [d.address] : []),

      zipcode: overwrittenFields.includes("zipcode")
      ? null
      : (d.zipcode !== this.zipcode ? [d.zipcode] : []),

      city: overwrittenFields.includes("city")
      ? null
      : (d.city !== this.city ? [d.city] : []),

      latitude: overwrittenFields.includes("latitude")
      ? null
      : (distanceGreaterThan(d.lat, this.latitude) ? [d.lat] : []),

      longitude: overwrittenFields.includes("longitude")
      ? null
      : (distanceGreaterThan(d.long, this.longitude) ? [d.long] : []),

      country: overwrittenFields.includes("country")
      ? null
      : (!stringComparaison(d.country, this.country) ? [d.country] : []),

      registrationNumber: overwrittenFields.includes("registrationNumber")
      ? null
      : (d.registrationNumber !== this.registrationNumber ? [d.registrationNumber] : []),
    };

  }




  /**
   * Dispatches a search action to the store to retrieve items based on the specified search term and provider.
   * Handles the response by assigning the result to the `items` property.
   * If there is an error during the process, it commits an error message to the store.
   * Updates the loading state upon completion.
   *
   * @return {void} This method does not return a value.
   */
  public querySelections() {
    if (this.loading || !this.search) {
      return;
    }
    this.loading = true;
    this.$store.dispatch("maps/searchByAddressName", { search: this.search, provider: this.provider }).then((res) => {
      this.items = res;
    }).catch((err) => {
      this.$store.commit("alerts/displayError", {
        msg: err,
      });
    }).finally(() => {
      this.loading = false;
    });
  }

  /**
   * Handles the Enter key press event. This method determines the context in which the Enter key
   * was pressed (input field, dropdown list, or elsewhere) and performs appropriate actions based
   * on the active element.
   *
   * @return {void} No return value.
   */
  public onEnter() {
    this.querySelections();
  }

  /**
   * This method retrieves the selected value and emits an event    *
   * @return {void}
   */
  public selectValue() {
    if (this.selected) {
      this.items = [];
    }
    if (this.selected) {
      this.emit(this.selected);
    }
  }

  /**
   * Overwrites the address with the given value and type.
   *
   * @param {any} value - The value to be set as the new address.
   * @param {string} type - The type of the address. This will be used as part of the event name to emit.
   * @return {void}
   */
  public overwriteAddress(value: string, type: string) {
    this.$emit(`update:${type}`, value);
    this.selected = null;
    this.items = [];
  }


  /**
   * Emits the given address details using Vue's event system.
   *
   * @param {object} address - The address object.
   * @param {string} address.address - The updated address.
   * @param {string} address.city - The updated city.
   * @param {string} address.zipcode - The updated zipcode.
   * @param {string} address.country - The updated country.
   * @param {number} address.latitude - The updated latitude.
   * @param {number} address.longitude - The updated longitude.
   *
   * @return {void}
   */
  public emit(address: MylobyPlace) {
    this.$emit("update:address", address.address);
    this.$emit("update:city", address.city);
    this.$emit("update:zipcode", address.zipcode);
    this.$emit("update:country", address.country);
    this.$emit("update:latitude", address.latitude);
    this.$emit("update:longitude", address.longitude);
  }


}
